import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const PdfViewer = ({ editor }) => {
  const [pdfUrl, setPdfUrl] = useState("https://www.aeee.in/wp-content/uploads/2020/08/Sample-pdf.pdf");
  const [compiling, setCompiling] = useState(false);
  const [validating, setValidating] = useState(false);
  const [skipDtd, setSkipDtd] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const isDevPort = typeof window !== "undefined" && window.location.port === "3000";
  const pdfServiceBase = isDevPort ? "http://localhost:8005" : (typeof window !== "undefined" ? `${window.location.origin}/pdf` : "/pdf");

  const handleCompile = async () => {
    if (!editor) {
      toast.error("Editor not initialized");
      return;
    }
    setCompiling(true);
    setValidationResult(null);

    try {
      const xmlContent = editor.getContent();
      const response = await axios.post(
        `${pdfServiceBase}/convert?skip_validation=${skipDtd}`,
        {
          xml: xmlContent,
          filename: "manuscript.xml",
        },
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      toast.success("PDF Compiled successfully!");
    } catch (err) {
      console.error("PDF Compile error:", err);
      toast.error("Failed to compile PDF. Check XML structure.");
    } finally {
      setCompiling(false);
    }
  };

  const handleDtdCheck = async () => {
    if (!editor) {
      toast.error("Editor not initialized");
      return;
    }
    setValidating(true);
    setValidationResult(null);

    try {
      const xmlContent = editor.getContent();
      const response = await axios.post(`${pdfServiceBase}/validate`, {
        xml: xmlContent,
        filename: "manuscript.xml",
      });

      setValidationResult({
        valid: response.data.valid,
        message: response.data.message || "XML validation passed with no errors.",
      });
      if (response.data.valid) {
        toast.success("JATS DTD Validation Passed!");
      } else {
        toast.error("DTD Validation failed.");
      }
    } catch (err) {
      console.error("DTD Check error:", err);
      if (err.response && err.response.data) {
        setValidationResult({
          valid: false,
          message: err.response.data.message || "Validation failed.",
        });
      } else {
        toast.error("Failed to execute DTD Validation.");
      }
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Action Toolbar Row */}
      <div className="flex items-center gap-2 border-b border-[#E7E4DD] pb-3 mb-3">
        <button
          onClick={handleCompile}
          disabled={compiling}
          className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#4F46E5] hover:bg-[#4338CA] transition-colors disabled:opacity-50"
        >
          {compiling ? "Compiling..." : "Compile PDF"}
        </button>
        <button
          onClick={handleDtdCheck}
          disabled={validating}
          className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-[#17171A] bg-[#FAF9F6] border border-[#E7E4DD] hover:bg-[#F4F3EF] transition-colors disabled:opacity-50"
        >
          {validating ? "Validating..." : "DTD Check"}
        </button>
        <label className="flex items-center gap-1.5 text-[11px] font-medium text-[#555] whitespace-nowrap cursor-pointer select-none">
          <input
            type="checkbox"
            checked={skipDtd}
            onChange={(e) => setSkipDtd(e.target.checked)}
            className="rounded border-[#E7E4DD] text-[#4F46E5] focus:ring-[#4F46E5]"
          />
          Skip DTD
        </label>
      </div>

      {/* Validation Result Box */}
      {validationResult && (
        <div
          className={`p-3 rounded-lg text-xs font-medium mb-3 border ${
            validationResult.valid
              ? "bg-[#E8F8F5] text-[#196F3D] border-[#D5F5E3]"
              : "bg-[#FDEDEC] text-[#943126] border-[#FADBD8]"
          }`}
        >
          <div className="font-bold mb-1">{validationResult.valid ? "✓ JATS DTD Validation Passed" : "✕ DTD Validation Failed"}</div>
          <div className="font-mono whitespace-pre-wrap leading-relaxed max-h-[100px] overflow-auto">{validationResult.message}</div>
        </div>
      )}

      {/* PDF Iframe */}
      <div className="flex-1 rounded-xl overflow-hidden border border-[#E7E4DD] bg-[#FAF9F6] relative min-h-[300px]">
        {pdfUrl ? (
          <iframe src={pdfUrl} className="w-full h-full border-none" title="PDF Viewer" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-[#9A988F]">
            Click "Compile PDF" to render PDF preview
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfViewer;
